import {InventoryRequest} from "../../../../api/Logistics";
import {PhaseViewCol} from "../openings/PhaseCol";
import {StatusCol} from "../openings/Openings";
import {api} from "../../../../api/API";
import {CheckboxSelectorCol} from "../Checkbox";
import {LogisticsView} from "./ViewButton";
import {releaseColumns, releaseListParams, releasePrefName, reviewStatusPendingColor} from "./ReleaseColumns";
import {useProjectUnitSystem} from "../ProjectName";
import {
    actionsColName,
    AdjustCol, CentsCol,
    Column,
    NumberCol, Option,
    RenderFilterProps,
    RenderStringCol, SelectInput, sortDate, sortNumericInner,
    StringCol,
    Table,
    ViewOnly
} from "../../../../misc/scroller/Table";
import {DateFilterCol, DateFilterForCol, dateFilterMatch} from "../../../../misc/DateFilterForCol";
import {FakeLink} from "../../../../misc/FakeLink";
import moment from "moment/moment";
import {Tooltip} from "@mui/material";
import {logisticsTableName, LogisticsTab} from "./Logistics";
import {useState} from "react";
import {useProjectId} from "../ProjectName";
import {ChangeHistory} from "../openings/ChangeHistory";
import {Checkbox2Col} from "../ShopTableCol";
import {getAll} from "../../../logistics/routes/useGetAll";
import {prepsAsString, woodDetailAsString} from "../../../logistics/warehouse/InventoryFrameTable";
import { formatCents } from "nate-react-api-helpers";
import {costEachColumnName, markupColumnName} from "../pricing/Const";
import {hasPermission, useUser} from "../../../../misc/Permission";
import {getActualProfit, getMargin, profit, renderMarkup, renderUnitPrice} from "../pricing/usePricingKit2";
import {User} from "../../../../api/Users";
import {ucWords} from "../pricing/PriceRequestSubmittal";
import {RowActions} from "./RowActions";

export function Openings(props: {
    type: Exclude<LogisticsTab, "div10" | "damaged" | "waste">
    view: LogisticsView;
    nonStockingForRelease?: number;
}) {
    const [showChangeHistoryForOpening, setShowChangeHistoryForOpening] = useState<{
        id: number; name: string, inventoryRequest?: {
            id: number;
            qty: number;
            status: string;
            inventoryItem?: number;
            kind?: string;
            stocking?: boolean;
        }
    }>();

    const project = useProjectId();
    const u = useUser();
    const sys = useProjectUnitSystem();
    const showOpeningInfo = props.type === "frame-hm" || props.type === "door-hm" || props.type === "door-wd" || props.type === "hardware" || props.type === "frame-anchor";
    if(!u) return null;
    if(!sys) return null;

    const isRelease = props.view === "release";
    const releaseCols = isRelease ? releaseColumns(props.type, sys) : null;

    return (
        <>
            <Table<InventoryRequest>
                key={props.view}
                name={logisticsTableName(project, props.type)}
                overridePrefsName={isRelease ? releasePrefName(project, props.type) : undefined}
                globalPrefsName={isRelease ? releasePrefName(0, props.type) : logisticsTableName(0, props.type)}
                fetch={ctx => getAll(ctx, offset => api.logistics.listInventoryRequest({
                    project: project,
                    offset: offset,
                    category: props.type,
                    nonStockingForRelease: props.nonStockingForRelease,
                    ...releaseListParams(props.view, props.type),
                }))}
                fetchDeps={[project, props.type, props.nonStockingForRelease, props.view]}
                columnDeps={[u, props.type, props.view, sys]}
                cellCustomize={isRelease && props.type === "hardware" ? {
                    backgroundColor: rw => {
                        if(rw.reviewStatus === "pending") {
                            return reviewStatusPendingColor
                        }
                    }
                } : undefined}
                columns={releaseCols || [
                    CheckboxSelectorCol(),
                    AdjustCol(ViewOnly(NumberCol("Inv Req ID", "id", 80)), {hiddenByDefault: true}),
                    {
                        name: actionsColName,
                        noCsv: true,
                        render: (o: InventoryRequest) => <RowActions row={o} type={props.type} />,
                        renderName: () => null,
                        width: 40,
                    },
                    showOpeningInfo && ViewOnly(NumberCol("Seq #", "seqNumber", 60)),
                    ViewOnly(StringCol("Opening", "openingName", 100)),
                    showOpeningInfo && ViewOnly(StringCol("Floor", "floor", 60)),
                    showOpeningInfo && PhaseViewCol<InventoryRequest>("phase", 60),
                    ViewOnly(Checkbox2Col("Ext", "exterior", {disabled: (o) => true}) as any),
                    ViewOnly(StringCol("Product", "name", 100)),
                    ViewOnly(StringCol("Manufacturer", "manufacturerName", 120)),
                    RenderStringCol<InventoryRequest>("Code", r => {
                        if(r.productCode === "Keying" && r.keyingType) return `${r.productCode} (${ucWords(r.keyingType.replace("-", " "))})`;
                        return r.productCode;
                    }),
                    ViewOnly(StringCol("Finish", "finish", 100)),
                    ViewOnly(StringCol("Dim", "dimensions", 100)),
                    props.type === "glazing" && ViewOnly(StringCol("Type", "glass.type", 90)),
                    props.type === "glazing" && ViewOnly(StringCol("Thickness", "glass.thickness", 90)),
                    props.type === "glazing" && ViewOnly(StringCol("Bead", "glass.bead", 90)),
                    (props.type === "door-hm" || props.type === "door-wd") && ViewOnly(StringCol("Door Elev", "door.elevation", 80)),
                    props.type === "frame-hm" && ViewOnly(StringCol("Screen Elev", "frame.screenElev", 100)),
                    props.type !== "hardware" && ViewOnly(RenderStringCol<InventoryRequest>("Preps", d => {
                        if(d.type === "door") {
                            return prepsAsString(d.door?.preps);
                        } else if(d.type === "frame") {
                            return prepsAsString(d.frame?.preps);
                        }

                        return ""
                    }, 100)),
                    props.type === "door-wd" && ViewOnly(RenderStringCol<InventoryRequest>("Wood Detail", d => {
                        if(d.type === "door") {
                            return woodDetailAsString(d.door?.woodDetail);
                        }

                        return ""
                    }, 100)),
                    ViewOnly(NumberCol("Qty", "qty", 50)),
                    StatusCol("Status",  "status"),
                    SourceCol(),
                    {
                        name: "Meta",
                        render: (data) => {

                            return (
                                <>
                                    <FakeLink onClick={() => setShowChangeHistoryForOpening({
                                        id: data.opening,
                                        name: data.openingName,
                                        inventoryRequest: {
                                            id: data.id,
                                            status: data.status,
                                            qty: data.qty,
                                            inventoryItem: data.inventoryItem,
                                            kind: data.type,
                                            stocking: data.isStocking,
                                        }
                                    })}>
                                        {moment(data.updatedAt).format("MMM D")} by {data.updatedByName}
                                    </FakeLink>
                                    {data.followupRequest && <>
                                        <Tooltip title={`Expected to receive this entire order, but ${data.receivedQty} items came in. A follow up order request has been created.`}><div>*Warning</div></Tooltip>
                                    </>}
                                    {data.isWaste && <>
                                        <Tooltip title={`This item was marked as waste. See waste tab for more details`}><div>*Warning</div></Tooltip>
                                    </>}
                                </>
                            );
                        },
                        sort: (a, b) => {
                            const result = moment(a.updatedAt).unix() - moment(b.updatedAt).unix()
                            if(result === 0) return result;
                            if(result < 0) return -1;
                            return 1;
                        },
                        width: 120,
                    },
                    {
                        name: "Needed By",
                        render: (o: InventoryRequest) => {
                            const v = o.neededBy
                            if(!v) return "";

                            return (
                                <div>
                                    {moment.utc(v).format("MMMM D")}
                                    {" "}({o.neededByCalcManuallySet ? "Manual" : o.neededByCalcReleaseSet ? "Release" : "Timeline"})
                                </div>
                            )
                        },
                        editable: {type: "date", format: "MMMM D"},
                        editKey: "neededBy",
                        width: 200,
                        filter: dateFilterMatch("neededBy", "future"),
                        renderFilter: (p: RenderFilterProps) => <DateFilterForCol value={p.value} onChange={p.onChange} mode="future" />,
                        sort: sortDate("neededBy"),
                        nullable: true,
                    },
                    DateFilterCol<InventoryRequest>("Delivered", "deliveredAt", "MMM D, YYYY", 110),
                    RenderStringCol<InventoryRequest>("Cogs", v => {
                        if(v.reportPayablePrice !== null) return formatCents(v.reportPayablePrice) + " (Bill)";
                        if(v.reportPurchasedPrice !== null) return formatCents(v.reportPurchasedPrice) + " (Purchased)";
                        if(v.reportQuotedPrice !== null) return formatCents(v.reportQuotedPrice) + " (Quoted)";
                        return "";
                    }),
                    ...PriceColumns(u),
                    ViewOnly(RenderStringCol<InventoryRequest>("Opening Archived", d => d.openingArchived ? "Yes" : "No", 130)),
                ]}
                onChange={input => api.logistics.updateInventoryRequest(input)}
            />
            {showChangeHistoryForOpening && <ChangeHistory opening={showChangeHistoryForOpening} inventoryRequest={showChangeHistoryForOpening.inventoryRequest} onDone={() => setShowChangeHistoryForOpening(undefined)} />}
        </>
    )
}

export function PriceColumns(u: User) {
    return [
        ViewOnly(CentsCol(costEachColumnName, "price.unitCostCents", 80)),
        AdjustCol(RenderStringCol(markupColumnName, (dt: InventoryRequest) => renderMarkup(dt.price)), {
            alignRight: true,
            width: 80,
            isChangedSinceBackup: (row: InventoryRequest) => {
                if(!row.priceBackup) return false;
                return row.price.markupCents !== row.priceBackup?.markupCents || row.price.markupPercent !== row.priceBackup?.markupPercent
            },
        }),
        {
            name: "Unit Price",
            render: (dt: InventoryRequest) => formatCents(renderUnitPrice(dt.price)),
            sort: (a: InventoryRequest, b: InventoryRequest) => sortNumericInner(renderUnitPrice(a.price), renderUnitPrice(b.price)),
            alignRight: true,
            width: 80,
        }, {
            name: "Price",
            alignRight: true,
            render: (dt: InventoryRequest) => formatCents(dt.price.extendedPrice),
            sort: (a: InventoryRequest, b: InventoryRequest) => sortNumericInner(a.price.extendedPrice, b.price.extendedPrice),
        },
        hasPermission(u, "CanViewProjectPricingProfit") && {
            name: "Proj. Profit",
            render: (dt: InventoryRequest) => formatCents(profit(dt.price)),
            alignRight: true,
            width: 90,
            sort: (a: InventoryRequest, b: InventoryRequest) => sortNumericInner(profit(a.price), profit(b.price)),
        },
        hasPermission(u, "CanViewProjectPricingProfit") && {
            name: "Act. Profit",
            render: (dt: InventoryRequest) => {
                const act = getActualProfit(dt.price)
                if(act === null) return ""
                return formatCents(act)
            },
            sort: (a: InventoryRequest, b: InventoryRequest) => sortNumericInner(getActualProfit(a.price) || 0, getActualProfit(b.price) || 0),
            alignRight: true,
            width: 90,
        },
        {
            name: "Proj. Margin",
            render: (dt: InventoryRequest) => {
                return getMargin(dt.price)
            },
            alignRight: true,
            width: 100,
        },
    ]
}

export function SourceCol(): Column<InventoryRequest> {
    return AdjustCol(RenderStringCol("Source", (o: InventoryRequest) => {
        if(o.overrideSource === 'purchase') {
            return "Purchase";
        }

        if(o.overrideSource === "non-stocking") {
            return "Warehouse"
        }

        if(o.isNonStocking && o.hasEnoughStock && o.productCode !== "Keying") {
            return "Use stock?"
        }

        return "";
    }), {
        width: 100,
        disabled: (o) => {
            if(!o.overrideSource) {
                if (!o.isNonStocking && !o.isStocking) return true;
            }

            if(o.status === "in-warehouse" || o.status === "released") return false;
            return true;
        },
        editable: {
            type: "select",
            options: (data: SelectInput<InventoryRequest>) => {
                let list: Option[] = [
                    {value: "non-stocking", display: "Warehouse"},
                    {value: "purchase", display: "Purchase"},
                ];

                return list;
            },
        },
        editKey: "overrideSource",
    })
}